import PropTypes from 'prop-types';
import { TableCell, TableSortLabel, Tooltip, tooltipClasses } from '@mui/material';
import { styled } from '@mui/material/styles';
import { memo, useRef, forwardRef } from 'react';
import ArrowIcon from '@/assets/icons/arrow-down-small.svg?react';
import { Palette } from '@/themes/themeOverrides/Palette';
import { useTranslate } from 'react-admin';

const CustomArrowIcon = forwardRef((props, ref) => (
    <div ref={ref} {...props}>
        <ArrowIcon />
    </div>
));

CustomArrowIcon.displayName = 'CustomArrowIcon';

const StyledTableCell = styled(TableCell)(() => ({
    backgroundColor: `${Palette.gray.gray100} !important`,
    borderBottom: `1px solid ${Palette.gray.gray75}`,
    fontWeight: 700,
    lineHeight: '16px !important',
    position: 'relative',
    '& .MuiTableSortLabel-root': {
        '& .MuiTableSortLabel-icon': {
            fill: Palette.gray.gray500,
        },
    },
}));

const LightTooltip = styled(({ className, ...props }) => <Tooltip {...props} classes={{ popper: className }} />)(() => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: Palette.black,
        color: Palette.white,
        boxShadow: Palette.boxShadow.elevation8,
        fontSize: 11,
        marginTop: '0 !important',
    },
    [`& .${tooltipClasses.arrow}`]: {
        color: Palette.black,
    },
    [`& .${tooltipClasses.popper}`]: {
        position: 'absolute !important',
        zIndex: 1,
    },
}));

const CustomDatagridHeaderCell = ({ className, field, sort, updateSort, isSorting, ...rest }) => {
    const translate = useTranslate();
    const arrowRef = useRef(null);
    if (!field) return null;

    const sortByLabel = field.props.label?.props?.sortByLabel || field.props.label?.props?.title || field.props.label;

    const sortLabel = `${translate('common.sortBy')} ${sortByLabel}`;

    return (
        <StyledTableCell className={className} align={field.props.textAlign} style={field.props.headerStyle} {...rest}>
            {updateSort && field.props.sortable !== false && (field.props.sortBy || field.props.source) ? (
                <LightTooltip
                    title={sortLabel}
                    placement='bottom'
                    enterDelay={300}
                    arrow
                    classes={{ popper: className }}
                    slotProps={{
                        popper: {
                            anchorEl: {
                                getBoundingClientRect: () => {
                                    const rect = arrowRef.current?.getBoundingClientRect();
                                    return new DOMRect(rect?.x || 0, rect?.y + 7 || 0, rect?.width || 0, rect?.height || 0);
                                },
                            },
                        },
                    }}
                >
                    <TableSortLabel
                        active={isSorting}
                        direction={sort?.order === 'ASC' ? 'asc' : 'desc'}
                        data-field={field.props.sortBy || field.props.source}
                        data-order={field.props.sortByOrder || 'ASC'}
                        onClick={updateSort}
                        IconComponent={CustomArrowIcon}
                        classes={{ icon: `arrow-icon ${sort?.order === 'ASC' ? 'arrow-icon-rotate' : ''}` }}
                        ref={arrowRef}
                    >
                        {field.props.label}
                    </TableSortLabel>
                </LightTooltip>
            ) : (
                field.props.label
            )}
        </StyledTableCell>
    );
};

CustomDatagridHeaderCell.propTypes = {
    className: PropTypes.string,
    field: PropTypes.element,
    sort: PropTypes.shape({
        field: PropTypes.string,
        order: PropTypes.oneOf(['ASC', 'DESC']),
    }),
    isSorting: PropTypes.bool,
    updateSort: PropTypes.func,
};

export default memo(
    CustomDatagridHeaderCell,
    (props, nextProps) =>
        props.updateSort === nextProps.updateSort &&
        props.sort?.field === nextProps.sort?.field &&
        props.sort?.order === nextProps.sort?.order &&
        props.isSorting === nextProps.isSorting,
);
